// Per-turn focus context for the dashboard-charts agent: names the artifact the user is currently
// viewing (the focused chart and/or dashboard) so a refinement edits it in place. Company-level
// grounding — KPIs, custom segments, sources — is NOT injected from the frontend; the agent grounds
// itself from the catalog's `companyContext` via the querying MCP tool.

import { equals } from 'ramda';
import {
  DimensionId,
  CardSize,
  ChartConfig,
  ResolvedRoleLevels
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterNode } from '@blisspointmedia/bpm-types/dist/querying';
import { isDraftId } from '../Charts/chart-handle';
import { levelToAudience, roleLevelLabel } from '../Charts/chart-permissions';

// A short summary of one saved chart, used to name an editable artifact to the agent by its id.
export interface FocusedChartSummary {
  id: string;
  title?: string;
  type?: string;
}

// The artifact the user is currently viewing. Built per-turn (not once-per-session) because focus
// changes as the user navigates — on a standalone chart view it's that chart; on a dashboard it's
// the dashboard plus the charts currently on it. Lets the agent reference an existing artifact by
// id so a refinement edits it in place rather than minting a duplicate.
// Both the focused single chart AND each dashboard chart carry their full `config` so edits are
// FAITHFUL — the agent changes only what the user asks and keeps everything else (queries, dateRange,
// title) identical, rather than rebuilding from a guess. This is what lets a SURGICAL edit of a
// dashboard chart ("remove the filter", "add revenue") work: without the config the agent can't see
// what to change and either refuses or rebuilds the chart from scratch.
export interface FocusedDashboardChart extends FocusedChartSummary {
  config?: ChartConfig;
  // The size of this chart's CARD on this dashboard. A placement fact, not a chart fact — the same
  // chart can be a different size elsewhere — which is why it hangs off the dashboard summary rather
  // than off FocusedChartSummary. Without it the agent can call set_card_size but cannot see what it
  // is changing from, so "make it smaller" is a guess and a size the frontend corrected is invisible.
  size?: CardSize;
  // The card's 1-based place in the dashboard's card order — the position move_card
  // takes. Off the layout on screen, so a pending move is already reflected.
  slot?: number;
  // Dimensions this card keeps all values of, whatever the dashboard filters say. Off the layout on
  // screen, so a pending opt-out is already reflected.
  ignoreDashboardFilters?: DimensionId[];
}
export interface FocusedArtifact {
  chart?: FocusedChartSummary & {
    config?: ChartConfig;
    sharing?: ResolvedRoleLevels;
    canEdit?: boolean;
  };
  dashboard?: {
    id: string;
    name?: string;
    charts: FocusedDashboardChart[];
    // The window the dashboard is showing, as concrete dates.
    date?: { start: string; end: string };
    // The saved default filters, and the ones on screen. Absent `currentFilters` means no live view.
    defaultFilters?: FilterNode[];
    currentFilters?: FilterNode[];
    sharing?: ResolvedRoleLevels;
    canEdit?: boolean;
  };
  /**
   * The role LEVELS this user may assign, ascending. Named for what it HOLDS, not for the
   * `ASSIGNABLE AUDIENCES` line it renders into — a field called `audiences` holding numbers is
   * exactly the confusion this feature keeps having to undo.
   *
   * Absent means "say nothing about sharing", which is what a page that does not know the caller's
   * role should do rather than implying a default the agent would then act on.
   */
  assignableLevels?: number[];
}

const formatChartSummary = (c: FocusedChartSummary & { size?: CardSize }): string =>
  `  ${c.id}${c.title ? ` "${c.title}"` : ''}${c.type ? ` [${c.type}]` : ''}${
    c.size ? ` (${c.size})` : ''
  }`;

// One level, as the agent names it. An audience NAME when this product has one, and the human label
// otherwise — a row can hold an unoffered level (2, 3) or an off-scale one (4), and the agent has to
// be able to DESCRIBE such a record even though it can never set that level.
const describeLevel = (level: number): string => levelToAudience(level) ?? roleLevelLabel(level);

// A SEMICOLON between the two clauses, not a comma. An unnameable level falls back to a label that
// itself contains a comma ("Tinuiti internal, including NOC"), so a comma here produces four
// comma-separated fragments and blurs where the first clause ends — in a line the agent may repeat
// verbatim to a user about who can see their chart.
const formatSharing = (sharing: ResolvedRoleLevels): string =>
  `  CURRENT SHARING: visible to ${describeLevel(sharing.viewRoleRequired)}; editable by ${describeLevel(
    sharing.editRoleRequired
  )}`;

// Whether THIS USER may edit this record — a fact, not an instruction. What to do about it (refuse an
// update in prose, offer save-as-new) lives once in the agent's system prompt; repeating it here would
// inject the same instruction on every turn. Absent means the page doesn't know yet (say nothing,
// matching `sharing`'s own absent case) — implying access the agent would then act on is worse than
// silence, since a wrongly-assumed "view only" would refuse a chart the user can actually edit, and a
// wrongly-assumed "can edit" is exactly the lie this feature exists to prevent.
const formatAccess = (canEdit: boolean, noun: string): string =>
  `  YOUR ACCESS: ${canEdit ? `you can change this ${noun}` : 'view only'}`;

// A draft has no row and therefore no audience. Said explicitly rather than left silent, because the
// agent may still record a request for it and has to describe that honestly.
// A FACT, NOT A RULE. What to DO about a draft lives once in the system prompt's "A draft is not
// shared yet" section. Restating it here would inject the same instruction on every turn, and a rule
// kept in two places drifts.
const NOT_SHARED_YET = '  CURRENT SHARING: not shared yet (never saved)';

const formatFocusedChart = (chart: NonNullable<FocusedArtifact['chart']>): string => {
  const lines = [
    'FOCUSED CHART (the user is viewing this single chart):',
    formatChartSummary(chart)
  ];
  // An absent `sharing` and a draft are two different things, and one string cannot serve both: an
  // absent `sharing` means the page does not know yet (say nothing), a draft means there is
  // definitively no audience (say so).
  if (chart.sharing) {
    lines.push(formatSharing(chart.sharing));
  } else if (isDraftId(chart.id)) {
    lines.push(NOT_SHARED_YET);
  }
  // Absent (`undefined`) says nothing — see `formatAccess`'s doc comment above.
  if (chart.canEdit !== undefined) {
    lines.push(formatAccess(chart.canEdit, 'chart'));
  }
  if (chart.config) {
    lines.push(
      '  Its CURRENT config is below — this is the AUTHORITATIVE, up-to-the-moment state of the chart.',
      '  It reflects what the user is actually looking at right now. TRUST IT OVER ANYTHING EARLIER IN',
      '  THIS CONVERSATION: between turns the user may have kept, discarded, or otherwise changed edits',
      '  outside the chat, so any config you emitted earlier may no longer apply. Do NOT assume a change',
      '  from a previous turn is still present unless it appears in THIS config. To REFINE it ("make it',
      '  two lines", "change to bar", "add revenue", etc.), edit THIS exact config: change ONLY what the',
      '  user asks and keep everything else identical (queries, dateRange, dimensions, and the title',
      '  unless they ask to rename it), then re-emit via emit_chart_config reusing the SAME id so the app',
      '  knows which chart you are refining. Your edit renders as an UNSAVED PREVIEW — emitting does NOT',
      '  save the chart or change it anywhere; the user decides whether to keep it. Do NOT say the chart',
      '  was saved, updated, or changed everywhere — just describe what you changed. Do NOT rebuild from',
      '  scratch or invent fields.',
      `  CURRENT CONFIG: ${JSON.stringify(chart.config)}`
    );
  } else {
    lines.push(
      `  To REFINE this chart, re-emit emit_chart_config with "id": "${chart.id}" and the full updated`,
      '  config (change only what the user asks). Your edit renders as an UNSAVED PREVIEW — emitting',
      '  does NOT save the chart; the user decides whether to keep it.'
    );
  }
  return lines.join('\n');
};

// One dashboard chart's line, plus its CURRENT config when known (so a surgical edit is faithful).
// Deliberately NO sharing here: a card's sharing cannot be changed from chat (only the chart's own
// page can), so naming its audiences would invite exactly the tool call the design refuses.
const formatDashboardChart = (c: FocusedDashboardChart): string => {
  const summary = formatChartSummary(c);
  const line = c.slot ? `  ${c.slot}. ${summary.trimStart()}` : summary;
  const optOut = c.ignoreDashboardFilters?.length
    ? `\n      IGNORES DASHBOARD FILTERS ON: ${c.ignoreDashboardFilters.join(', ')}`
    : '';
  if (!c.config) return `${line}${optOut}`;
  return `${line}${optOut}\n      CURRENT CONFIG: ${JSON.stringify(c.config)}`;
};

// Nothing is said when there are no filters at all. The current line is dropped when it equals the
// default, so the agent never reads a copy of the default as a user change.
const formatDashboardView = ({
  date,
  defaultFilters = [],
  currentFilters
}: NonNullable<FocusedArtifact['dashboard']>): string[] => {
  const currentDiffers = currentFilters !== undefined && !equals(currentFilters, defaultFilters);
  return [
    ...(date ? [`  DASHBOARD DATE: ${date.start}..${date.end}`] : []),
    ...(defaultFilters.length
      ? [`  DEFAULT DASHBOARD FILTERS: ${JSON.stringify(defaultFilters)}`]
      : []),
    ...(currentDiffers ? [`  CURRENT DASHBOARD FILTERS: ${JSON.stringify(currentFilters)}`] : [])
  ];
};

const formatFocusedDashboard = (dashboard: NonNullable<FocusedArtifact['dashboard']>): string => {
  const charts = dashboard.charts.length
    ? [
        '  Charts currently on it, numbered in dashboard order — that number',
        "  is the card's position for move_card. Each line is followed by that chart's CURRENT config",
        '  (the AUTHORITATIVE, up-to-the-moment state) when available:',
        ...dashboard.charts.map(formatDashboardChart)
      ].join('\n')
    : '  (no charts on it yet)';
  return [
    `FOCUSED DASHBOARD: ${dashboard.name ?? '(unnamed)'} (${dashboard.id})`,
    // A dashboard has no draft state, so an absent pair simply says nothing — unlike a chart, there
    // is no "not shared yet" case to distinguish here.
    ...(dashboard.sharing ? [formatSharing(dashboard.sharing)] : []),
    // Absent (`undefined`) says nothing — see `formatAccess`'s doc comment above.
    ...(dashboard.canEdit !== undefined ? [formatAccess(dashboard.canEdit, 'dashboard')] : []),
    ...formatDashboardView(dashboard),
    charts,
    '  To EDIT one of these charts ("change the spend chart to bar", "add revenue to the ROAS chart",',
    '  "remove its filter"), edit THAT chart\'s CURRENT config: change ONLY what the user asks and keep',
    '  everything else identical (queries, dateRange, dimensions, and the title unless they ask to',
    "  rename it), then re-emit via emit_chart_config reusing THAT chart's SAME id. Do NOT rebuild from",
    '  scratch or invent fields. Your edit renders as an UNSAVED PREVIEW on that card — it does NOT save',
    "  or change the chart anywhere; the user promotes it from the card's own controls (Update everywhere",
    "  it's used / Save as a new chart / Discard), so do NOT say it was saved or updated. To ADD a new",
    '  chart ("also show…", "add a chart for…"), emit ONE chart with a new id — it is appended to THIS',
    '  dashboard. Do NOT call emit_dashboard_config for an add/edit on an existing dashboard.',
    '  To change the filters that apply to the WHOLE dashboard ("filter to US", "clear the filters",',
    '  "remove the Amazon filter", or any filter request that names no single chart), call',
    '  set_dashboard_filters. To make ONE card keep every value of a dimension whatever the dashboard',
    '  filters say ("show all regions on this chart"), call set_card_dashboard_filter_optout. NEVER copy a',
    "  dashboard filter into a chart's own filters; the dashboard filters already apply to its cards."
  ].join('\n');
};

// Per-turn focus context: names the artifact the user is currently viewing so the agent can edit it
// in place by re-emitting with its id. Returns undefined when nothing is in focus (list/empty view).
export const buildFocusContext = (focus: FocusedArtifact): string | undefined => {
  const parts: string[] = [];
  if (focus.chart) {
    parts.push(formatFocusedChart(focus.chart));
  }
  if (focus.dashboard) {
    parts.push(formatFocusedDashboard(focus.dashboard));
  }
  // DATA ONLY, one line. What to do when a user asks for an audience that is not on this list --
  // refuse in prose, call nothing -- is stated once in the system prompt's "What you may assign"
  // section, as is the rule about cards. Repeating either here would re-send the same instruction on
  // every single turn and give one rule two homes to drift between.
  // `.length`, not a bare truthiness check: an empty array is truthy, and a bare
  // `ASSIGNABLE AUDIENCES: ` line reads to the agent as no constraint rather than no options. An
  // empty list means say nothing here — matching `assignableLevels`'s own doc comment above.
  if (focus.assignableLevels && focus.assignableLevels.length > 0) {
    parts.push(`ASSIGNABLE AUDIENCES: ${focus.assignableLevels.map(describeLevel).join(', ')}`);
  }
  return parts.length ? parts.join('\n\n') : undefined;
};
